import { el } from '../dom';
import { richText } from '../ui/icons';
import { onAction } from '../action/dispatch';

/**
 * The tutorial COACH — a pinned instruction card + optional world-anchored
 * pointer that walks the player through a list of steps, advancing when each
 * step's completion condition fires.
 *
 * It is deliberately engine-agnostic: steps describe *what to wait for*
 * (an action type, or a polled predicate), and the coach owns the presentation
 * (card, progress dots, Skip) and the bookkeeping. Nothing here touches the
 * PixiJS scene except an optional screen-space arrow positioned via a caller-
 * supplied world→screen function.
 */

export interface CoachStep {
  /** Short imperative instruction shown on the card. */
  text: string;
  /** Optional heading above the text (e.g. "Move", "Chop"). */
  title?: string;
  /** Auto-advance when an action of this type completes (see dispatch.onAction). */
  onActionType?: string;
  /** Auto-advance when this predicate turns true (polled ~6×/s). */
  until?: () => boolean;
  /** Static world tile the arrow should point at while this step is active. */
  arrow?: { tx: number; ty: number };
  /** Dynamic world tile (re-evaluated each frame) — e.g. the player's tilled bed. */
  arrowAt?: () => { tx: number; ty: number } | null;
  /**
   * Text for the off-screen chip — "Ore vein · 34 tiles ↗", say. Only rendered
   * when `arrowAt`'s target is outside the viewport, which is exactly when the
   * arrow alone cannot tell the player anything.
   */
  arrowLabel?: () => string | null;
  /** A real UI element to point at + ring — e.g. the Hoe hotbar slot, the Build button. */
  domAt?: () => Element | null;
  /** Fires once when the step becomes active (place props, focus camera, …). */
  onEnter?: () => void;
  /** Let the player click "Got it" to advance instead of doing the action. */
  manualOk?: boolean;
}

export interface CoachHandle {
  destroy(): void;
}

interface CoachOpts {
  /** Element the card + arrow overlay attach to (the farm view root `wrap`). */
  host: HTMLElement;
  steps: CoachStep[];
  /** Convert a world tile centre to on-screen px within `host` (for the arrow). */
  worldToScreen?: (tx: number, ty: number) => { x: number; y: number } | null;
  onDone: (skipped: boolean) => void;
  /** Card title prefix, e.g. "Tutorial · Basics". */
  chapter?: string;
  /**
   * Step to open on, so a reload resumes where the player left off rather than
   * replaying the chapter from "Welcome". Clamped to the last step: a stale index
   * saved against a longer steps array must not run off the end, because that
   * would call onDone and retire the tutorial for good with nothing shown.
   */
  startAt?: number;
  /** Called with the index of each step as it opens, for persistence. */
  onStep?: (idx: number) => void;
}

export function startCoach(opts: CoachOpts): CoachHandle {
  const { host, steps, worldToScreen, onDone, chapter, startAt, onStep } = opts;
  // advance() pre-increments, so seed one BEFORE the step we want to open on.
  // With no steps at all the seed stays at -1, so the first advance() falls
  // straight through to onDone instead of indexing off the front of the array —
  // a bug here must never wedge the farm.
  let idx = steps.length ? Math.min(Math.max((startAt ?? 0) - 1, -1), steps.length - 2) : -1;
  let alive = true;
  let unsub: (() => void) | null = null;
  let poll: number | null = null;
  let raf: number | null = null;

  // ─── Card ────────────────────────────────────────────────────────────────
  const titleEl = el('div', 'text-gold text-[16px] leading-tight mb-0.5');
  const textEl = el('div', 'text-[14px] text-[#e9d6bd] leading-snug');
  const dots = el('div', 'flex gap-1 mt-2');
  const nextBtn = el('button', 'tb-pixel-btn px-3 py-1 text-[13px] mt-2 hidden', { onClick: () => advance(false) }, 'Got it →') as HTMLButtonElement;
  const skipBtn = el('button', 'text-[12px] text-[#e9d6bd]/60 hover:text-[#e9d6bd] underline mt-1', { onClick: () => finish(true) }, 'Skip tutorial');
  // game-font on the whole card → the Terraboma pixel typeface everywhere in it.
  const card = el('div',
    'tb-coach game-font absolute left-1/2 -translate-x-1/2 top-4 z-[60] w-[380px] max-w-[92vw] rounded-xl px-4 py-3 shadow-lift',
    { style: 'background:linear-gradient(180deg,#3a2b1e,#241a12);border:2px solid #7a5c3a' },
    titleEl, textEl, dots, nextBtn,
    el('div', 'flex justify-between items-center', {}, el('span', ''), skipBtn));
  host.appendChild(card);

  // ─── Native pointer: a bouncing arrow + a highlight ring on the real target ─
  // A CSS triangle pointer (NOT an emoji) — a gold downward wedge, 22px wide.
  const arrow = el('div', 'tb-coach-arrow absolute z-[61] pointer-events-none', {
    style: 'width:0;height:0;border-left:11px solid transparent;border-right:11px solid transparent;'
      + 'border-top:16px solid #ffd75e;filter:drop-shadow(0 2px 0 rgba(0,0,0,.6))',
  });
  const ring = el('div', 'absolute z-[60] pointer-events-none', {
    style: 'border:3px solid #ffd75e;border-radius:8px;box-shadow:0 0 0 2px rgba(0,0,0,.5),0 0 14px rgba(255,215,94,.7);image-rendering:pixelated',
  });
  /**
   * The "it is over THAT way" chip, shown only when the target is off-screen.
   *
   * A world arrow used to be drawn at the raw projected position with no
   * clamping, so a target outside the viewport put it off the edge of the host —
   * i.e. nowhere. On the tutorial's mining step that is the whole instruction:
   * the player is told to click an ore vein, the arrow is invisible because the
   * nearest one is off-screen, and there is no way to find out which way to walk.
   * Owner: "tutorial should show where's the nearest ore is, to mine it."
   */
  const chip = el('div', 'absolute z-[61] pointer-events-none text-[11px] font-bold whitespace-nowrap px-1.5 py-0.5 rounded', {
    style: 'background:rgba(36,26,18,.92);border:2px solid #7a5c3a;color:#ffd75e;text-shadow:0 1px 0 rgba(0,0,0,.6)',
  });
  arrow.style.display = 'none';
  ring.style.display = 'none';
  chip.style.display = 'none';
  host.appendChild(ring);
  host.appendChild(arrow);
  host.appendChild(chip);

  const hostRect = () => host.getBoundingClientRect();

  function renderDots() {
    dots.replaceChildren(...steps.map((_, i) =>
      el('span', 'w-2 h-2 rounded-full', { style: `background:${i < idx ? '#7ac74f' : i === idx ? '#ffd75e' : '#6b5237'}` })));
  }

  function hide() { arrow.style.display = 'none'; ring.style.display = 'none'; chip.style.display = 'none'; }

  function tickArrow() {
    if (!alive) return;
    const step = steps[idx];
    const bob = Math.sin(Date.now() / 220) * 5;   // gentle bounce
    if (step?.domAt) {
      // Point at a REAL UI element: ring around it, arrow bobbing just above.
      const elh = step.domAt();
      // `getClientRects().length` — not just "the node exists". A node inside a
      // collapsed menu (or any `display:none` ancestor) still resolves from
      // querySelector but measures 0x0 at 0,0, which drew the highlight ring as a
      // tiny box in the corner and pointed the arrow at empty screen. Nothing
      // painted is honest; a ring on the wrong pixel is not.
      if (elh && elh.getClientRects().length) {
        const r = elh.getBoundingClientRect(), h = hostRect();
        const x = r.left - h.left, y = r.top - h.top;
        ring.style.display = ''; arrow.style.display = '';
        ring.style.left = `${x - 4}px`; ring.style.top = `${y - 4}px`;
        ring.style.width = `${r.width + 8}px`; ring.style.height = `${r.height + 8}px`;
        arrow.style.left = `${x + r.width / 2 - 11}px`;
        arrow.style.top = `${y - 36 + bob}px`;
      } else hide();
    } else if ((step?.arrowAt || step?.arrow) && worldToScreen) {
      const t = step.arrowAt ? step.arrowAt() : step.arrow!;
      const p = t ? worldToScreen(t.tx, t.ty) : null;
      if (p) {
        ring.style.display = 'none'; arrow.style.display = '';
        const h = hostRect();
        // Is the target actually on screen? The margin keeps the arrow clear of
        // the edge rather than half-clipped by it.
        const M = 46;
        const offX = p.x < M || p.x > h.width - M;
        const offY = p.y < M || p.y > h.height - M;
        if (!offX && !offY) {
          chip.style.display = 'none';
          arrow.style.transform = '';
          arrow.style.left = `${p.x - 11}px`;
          arrow.style.top = `${p.y - 44 + bob}px`;
        } else {
          // Off screen: pin the arrow to the edge nearest the target and turn it
          // to face outward, so it reads as a direction to walk rather than as a
          // marker on nothing. The bob rides along the same axis.
          const cx = h.width / 2, cy = h.height / 2;
          const dx = p.x - cx, dy = p.y - cy;
          const ex = Math.max(M, Math.min(h.width - M, p.x));
          const ey = Math.max(M, Math.min(h.height - M, p.y));
          arrow.style.left = `${ex - 11}px`;
          arrow.style.top = `${ey - 8 + bob * 0.4}px`;
          // The wedge points DOWN at rest, so 0deg already means "south".
          arrow.style.transform = `rotate(${(Math.atan2(dy, dx) * 180) / Math.PI - 90}deg)`;
          const label = step.arrowLabel?.() ?? null;
          if (label) {
            chip.style.display = '';
            chip.textContent = label;
            // Kept inside the host: a chip pinned at the right edge would
            // otherwise run off it.
            const cw = chip.offsetWidth || 60;
            chip.style.left = `${Math.max(4, Math.min(h.width - cw - 4, ex - cw / 2))}px`;
            chip.style.top = `${Math.max(4, Math.min(h.height - 24, ey + 18))}px`;
          } else chip.style.display = 'none';
        }
      } else hide();
    } else hide();
    raf = requestAnimationFrame(tickArrow);
  }

  function clearWaiters() {
    if (unsub) { unsub(); unsub = null; }
    if (poll !== null) { clearInterval(poll); poll = null; }
  }

  function advance(_skip: boolean) {
    if (!alive) return;
    clearWaiters();
    idx++;
    if (idx >= steps.length) { finish(false); return; }
    try { onStep?.(idx); } catch { /* persistence must not break the coach */ }
    const step = steps[idx];
    titleEl.textContent = `${chapter ? chapter + ' · ' : ''}${step.title ?? `Step ${idx + 1}`}`;
    // richText, not textContent: step copy carries ⚙️/🌱 markers that must render as
    // pack sprites — textContent leaked them to the screen as system glyphs.
    textEl.replaceChildren(...richText(step.text));
    nextBtn.classList.toggle('hidden', !step.manualOk);
    hide();
    renderDots();
    try { step.onEnter?.(); } catch { /* onEnter must not break the coach */ }

    // Arm the completion condition.
    if (step.onActionType) {
      unsub = onAction((type) => { if (type === step.onActionType) advance(false); });
    }
    if (step.until) {
      poll = window.setInterval(() => { try { if (step.until!()) advance(false); } catch { /* ignore */ } }, 160);
    }
  }

  function teardown() {
    alive = false;
    clearWaiters();
    if (raf !== null) cancelAnimationFrame(raf);
    card.remove();
    arrow.remove();
    ring.remove();
  }

  function finish(skipped: boolean) {
    if (!alive) return;
    teardown();
    onDone(skipped);
  }

  renderDots();
  advance(false);
  tickArrow();

  // destroy() is the SILENT teardown (e.g. the player switched tabs) — it does
  // NOT count as completing or skipping, so the tutorial resumes next visit.
  return { destroy: () => { if (alive) teardown(); } };
}
